import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  AgingBucket,
  InvoiceFlagBucket,
  InvoiceStatusBucket,
  MonthlyCostPoint,
} from '@faktorplus/shared';
import { useCostsByMonth, useInvoiceFlags, useInvoicesByStatus, usePayablesAging } from '../../api/reports';
import { Panel, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import {
  CashFlag,
  MissingProtocolFlag,
  StatusPill,
  WithoutOwnerFlag,
} from '../../components/ui/status-pill';
import { formatMoney, formatMoneyCompact } from '../../lib/format';
import styles from './dashboard-charts.module.css';

const MONTH_LABELS = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

/** A severity dot stands in for the aging bucket's colour. */
const AGING_DOT: Record<string, string> = {
  current: styles.dotOk!,
  '1-7': styles.dotWarn!,
  '8-30': styles.dotWarn!,
  '30+': styles.dotCrit!,
};

/** Net cost per month, last twelve months. */
export function CostTrendPanel() {
  const { data: points = [], isLoading, error } = useCostsByMonth();
  const hasAny = points.some((point) => point.netAmount > 0);

  return (
    <Panel title="Koszty miesięcznie — ostatnie 12 miesięcy">
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBox>Nie udało się pobrać trendu kosztów.</ErrorBox>
      ) : !hasAny ? (
        <p className={styles.empty}>Brak kosztów w tym okresie.</p>
      ) : (
        <CostTrendChart points={points} />
      )}
    </Panel>
  );
}

function CostTrendChart({ points }: { points: MonthlyCostPoint[] }) {
  const max = Math.max(...points.map((point) => point.netAmount), 1);

  return (
    <div className={styles.trend}>
      {points.map((point) => {
        const ratio = point.netAmount / max;
        const height = `${point.netAmount > 0 ? Math.max(ratio * 100, 3) : 0}%`;

        return (
          <div key={point.month} className={styles.trendCol}>
            <div className={styles.trendBarWrap}>
              <div className={styles.trendBar} style={{ height }}>
                <span className={styles.tip}>
                  <span className={styles.tipLabel}>{monthTip(point.month)}</span>
                  <span className={styles.tipValue}>{formatMoney(point.netAmount)}</span>
                </span>
              </div>
            </div>
            <span className={styles.trendLabel}>{monthShort(point.month)}</span>
          </div>
        );
      })}
    </div>
  );
}

/** How many invoices sit at each stage of the flow, and for how much. */
export function InvoiceStatusPanel({ period }: { period: { from?: string; to?: string } }) {
  const { data: buckets = [], isLoading, error } = useInvoicesByStatus(period);
  const { data: flags = [] } = useInvoiceFlags(period);
  const hasAny = buckets.some((bucket) => bucket.count > 0);
  const shownFlags = flags.filter((bucket) => bucket.count > 0);

  return (
    <Panel title="Obieg faktur wg statusu">
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBox>Nie udało się pobrać obiegu faktur.</ErrorBox>
      ) : !hasAny ? (
        <p className={styles.empty}>Brak faktur w tym okresie.</p>
      ) : (
        <div className={styles.rows}>
          {buckets.map((bucket) => (
            <StatusRow key={bucket.status} bucket={bucket} />
          ))}
          {shownFlags.length > 0 && (
            <>
              {/* Cut across every status, so they hang below the flow rather
                  than pretending to be a stage of it. */}
              <div className={styles.rowsDivider} role="presentation" />
              {shownFlags.map((bucket) => (
                <FlagRow key={bucket.flag} bucket={bucket} />
              ))}
            </>
          )}
        </div>
      )}
    </Panel>
  );
}

function StatusRow({ bucket }: { bucket: InvoiceStatusBucket }) {
  return (
    <Link
      to={`/faktury?status=${bucket.status}`}
      className={`${styles.row} ${styles.rowLink}`}
      title="Pokaż faktury z tym statusem"
    >
      <span className={styles.rowLabel}>
        <StatusPill status={bucket.status} />
      </span>
      <span className={styles.rowCount}>{bucket.count}</span>
      <span className={styles.rowValue}>{formatMoneyCompact(bucket.grossAmount)}</span>
    </Link>
  );
}

const FLAG_ROW: Record<
  InvoiceFlagBucket['flag'],
  { pill: () => ReactNode; href: string; title: string }
> = {
  CASH: {
    pill: () => <CashFlag />,
    href: '/faktury?onlyCash=true',
    title: 'Pokaż faktury płacone gotówką',
  },
  MISSING_PROTOCOL: {
    pill: () => <MissingProtocolFlag />,
    href: '/faktury?missingProtocol=true',
    title: 'Pokaż faktury bez protokołu odbioru',
  },
  WITHOUT_OWNER: {
    pill: () => <WithoutOwnerFlag />,
    href: '/faktury?withoutOwnerApproval=true',
    title: 'Pokaż faktury zatwierdzone do zapłaty bez właściciela',
  },
};

function FlagRow({ bucket }: { bucket: InvoiceFlagBucket }) {
  const row = FLAG_ROW[bucket.flag];
  return (
    <Link to={row.href} className={`${styles.row} ${styles.rowLink}`} title={row.title}>
      <span className={styles.rowLabel}>{row.pill()}</span>
      <span className={styles.rowCount}>{bucket.count}</span>
      <span className={styles.rowValue}>{formatMoneyCompact(bucket.grossAmount)}</span>
    </Link>
  );
}

/** Unpaid amounts bucketed by how overdue they are. */
export function AgingPanel() {
  const { data: buckets = [], isLoading, error } = usePayablesAging();
  const hasAny = buckets.some((bucket) => bucket.count > 0);

  return (
    <Panel title="Przeterminowanie zobowiązań">
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBox>Nie udało się pobrać przeterminowania.</ErrorBox>
      ) : !hasAny ? (
        <p className={styles.empty}>Brak niezapłaconych zobowiązań.</p>
      ) : (
        <div className={styles.rows}>
          {buckets.map((bucket) => (
            <AgingRow key={bucket.key} bucket={bucket} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function AgingRow({ bucket }: { bucket: AgingBucket }) {
  return (
    <Link
      to={agingHref(bucket.key)}
      className={`${styles.row} ${styles.rowLink}`}
      title="Pokaż rozrachunki z tego przedziału"
    >
      <span className={styles.rowLabel}>
        <span className={`${styles.dot} ${AGING_DOT[bucket.key] ?? styles.dotNeutral}`} />
        {bucket.label}
      </span>
      <span className={styles.rowCount}>{bucket.count}</span>
      <span className={styles.rowValue}>{formatMoneyCompact(bucket.amountDue)}</span>
    </Link>
  );
}

/**
 * The payables screen filters by due-date range, so each aging bucket becomes
 * the window of due dates that produces it: "1–7 dni" is everything that fell
 * due between seven days ago and yesterday.
 */
function agingHref(key: string): string {
  const daysAgo = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return toDateInput(date);
  };

  switch (key) {
    case 'current':
      return `/rozrachunki?dueFrom=${toDateInput(new Date())}`;
    case '1-7':
      return `/rozrachunki?dueFrom=${daysAgo(7)}&dueTo=${daysAgo(1)}`;
    case '8-30':
      return `/rozrachunki?dueFrom=${daysAgo(30)}&dueTo=${daysAgo(8)}`;
    case '30+':
      return `/rozrachunki?dueTo=${daysAgo(31)}`;
    default:
      return '/rozrachunki';
  }
}

function toDateInput(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function monthShort(month: string): string {
  const index = Number(month.slice(5, 7)) - 1;
  return MONTH_LABELS[index] ?? month;
}

function monthTip(month: string): string {
  const index = Number(month.slice(5, 7)) - 1;
  return `${MONTH_LABELS[index] ?? ''} ${month.slice(0, 4)}`;
}
